// Discreet mini-form for the paused makeup service. Captures interest
// so we don't lose bridal / event traffic while the service is offline.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MakeupWaitlist() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/makeup-waitlist`, {
        email: email.trim(),
        name: name.trim(),
        note: note.trim(),
      });
      setJoined(true);
      toast.success("You're on the list.", {
        description: "Bella will email you the moment makeup returns.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail.length
          ? detail.map((d) => d.msg).join("; ")
          : "Please try again or email Bella@triplebtherapy.com.";
      toast.error("Could not add you to the list", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="makeup-waitlist"
      data-testid="makeup-waitlist-section"
      className="relative py-16 md:py-20 bg-[#0F1F4E] text-[#F8F1D8] overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#EAD584] blur-3xl opacity-10" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-5">
            <div className="overline-gold inline-flex items-center gap-2 mb-4">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              Makeup — on pause
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light leading-tight tracking-tight text-[#F8F1D8]">
              Missing the <span className="italic text-[#EAD584]">makeup</span> menu?
            </h2>
            <p className="mt-4 text-sm md:text-base text-[#D6D6BD] font-light leading-relaxed">
              Bridal, natural, and glam makeup are paused for now. Leave your
              email and Bella will send a single, low-fuss note the day it
              returns — no spam, no newsletter.
            </p>
          </div>

          {joined ? (
            <div
              data-testid="makeup-waitlist-thanks"
              className="col-span-12 md:col-span-7 rounded-lg border border-[#EAD584]/40 bg-[#0A1838]/60 p-6 md:p-7"
            >
              <div className="overline-gold mb-2">You're on the list</div>
              <p className="text-[#F8F1D8] font-light text-base md:text-lg leading-relaxed">
                Thank you — Bella will email <strong>{email}</strong> the day
                makeup opens again. In the meantime,{" "}
                <a
                  href="#services"
                  className="underline text-[#EAD584] hover:text-[#F0DC8A]"
                >
                  browse massage &amp; facial services
                </a>
                .
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="makeup-waitlist-form"
              className="col-span-12 md:col-span-7 rounded-lg border border-[#EAD584]/30 bg-[#0A1838]/50 p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <label className="block sm:col-span-1">
                <span className="overline-gold block mb-1.5">Name (optional)</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="makeup-waitlist-name"
                  className="w-full bg-transparent border-b border-[#EAD584]/40 focus:border-[#EAD584] outline-none py-2 text-sm md:text-base text-[#F8F1D8] placeholder-[#D6D6BD]/50 font-light"
                  placeholder="Your name"
                  maxLength={120}
                />
              </label>
              <label className="block sm:col-span-1">
                <span className="overline-gold block mb-1.5">Email</span>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="makeup-waitlist-email"
                  className="w-full bg-transparent border-b border-[#EAD584]/40 focus:border-[#EAD584] outline-none py-2 text-sm md:text-base text-[#F8F1D8] placeholder-[#D6D6BD]/50 font-light"
                  placeholder="you@email.com"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline-gold block mb-1.5">
                  What are you hoping for? <span className="opacity-60">(optional)</span>
                </span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="makeup-waitlist-note"
                  className="w-full bg-transparent border-b border-[#EAD584]/40 focus:border-[#EAD584] outline-none py-2 text-sm md:text-base text-[#F8F1D8] placeholder-[#D6D6BD]/50 font-light"
                  placeholder="Bridal / event date / natural glam…"
                  maxLength={500}
                />
              </label>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 mt-1">
                <p className="text-[10px] uppercase tracking-wider text-[#D6D6BD]/70 max-w-xs">
                  One email when makeup returns — that's the only reason we'll
                  reach out.
                </p>
                <button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="makeup-waitlist-submit"
                  className="btn-shine inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs md:text-sm tracking-wider uppercase text-[#0F1F4E] bg-[#EAD584] hover:bg-[#F0DC8A] transition-colors disabled:opacity-60"
                >
                  {loading ? "Adding…" : "Notify me"}
                  {!loading && <Send className="w-3.5 h-3.5" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
